import { AttendeeType } from 'store/transactions/types';
import { FormattedAttendees } from 'store/cart/types';
import { VisitorsErrorConfig, VisitorTouched } from 'components/adding-visitors-block/types';
import { currentUser } from 'store/user/selectors';
import { formatAttendees } from 'components/payment/quick-checkout/components/utils/formatAttendees';
import { selectAllVisitorsEmails } from 'store/visitors-emails/selectors';
import { useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import visitorEmailsValidator from 'components/adding-visitors-block/validators/visitorEmailsValidator';
import { VisitorsEmailsState } from 'store/visitors-emails/types';

interface UseAddingVisitorsBlockReturnValues {
  visitors: VisitorsEmailsState;
  formattedAttendees: Array<FormattedAttendees>;
  errorConfig: VisitorsErrorConfig;
  markFieldAsTouched: (visitorId: number, fieldName: 'firstName' | 'lastName' | 'email') => void;
}

export const useAddingVisitorsBlock = (): UseAddingVisitorsBlockReturnValues => {
  const visitors: VisitorsEmailsState = useSelector(selectAllVisitorsEmails);
  const user = useSelector(currentUser);
  const [visitorTouched, setVisitorTouched] = useState<VisitorTouched>({});

  const formattedAttendees = useMemo<Array<FormattedAttendees>>(
    () => formatAttendees(visitors, AttendeeType.VISITOR),
    [visitors],
  );

  const validatorResult = useMemo(() => visitorEmailsValidator(visitors, user.email), [visitors, user]);

  const markFieldAsTouched = useCallback((visitorId: number, fieldName: 'firstName' | 'lastName' | 'email') => {
    setVisitorTouched(prev => ({
      ...prev,
      [visitorId]: {
        firstName: false,
        lastName: false,
        email: false,
        ...prev[visitorId],
        [fieldName]: true,
      },
    }));
  }, []);

  return {
    visitors,
    formattedAttendees,
    errorConfig: {
      ...validatorResult,
      visitorTouched,
    },
    markFieldAsTouched,
  };
};
